import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AnalyticsService, ICreateAppointmentPayload, PartnerBrandService, } from '@campfire-nx/api';

import { COMMON_CONSTANTS } from '@open-portal-app/core/constants/common.constants';
import { RequestService } from './request.service';
import { IStep, IUserDetails, IFlowPayload, IInfoBox } from '../models/request.models'
import { STEP_ONE, STEP_TWO, STEP_THREE, STEP_FOUR, TOPICS, ERRORS } from '../constants/request-flow-v001';

@Injectable({
  providedIn: 'root',
})
export class RequestFlowService {
  steps = [] as IStep[];
  currentStepIndex = 0;
  requestDetails = this.requestService.details;
  errorCode!: string;
  userDetails?: IUserDetails;

  constructor(
    private router: Router,
    private requestService: RequestService,
    private analyticsService: AnalyticsService,
    private partnerBrandService: PartnerBrandService
  ) {}

  next(payload: IFlowPayload) {
    return new Promise((resolve) => {
      if (this.currentStepIndex <= 2) {
        // update the current step so we have the data if we go back to it
        this.updateCurrentStep(payload);

        // update the next step in the flow then move to the next step
        this.currentStepIndex++;
        this.updateCurrentStep(payload);
        this.analyticsService.trackEvent('Initialise', 'RequestFlow', 'start-step', this.steps[this.currentStepIndex].analytics);
        resolve(this.steps[this.currentStepIndex]);
      } else if (this.currentStepIndex > 3) {
        // Reset currentStepIndex and go to dashboard
        this.currentStepIndex = 0;
        this.router.navigate(['/dashboard']);
      }
      if (this.currentStepIndex === 3) {

        this.requestAssistedSupport(payload, resolve);
      }
    });
  }

  back() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex = this.currentStepIndex - 1;
    } else if (this.currentStepIndex === 0) {
      return new Promise((resolve) => {
        this.router.navigate([`/support`]);
      });
    }

    return new Promise((resolve) => resolve(this.steps[this.currentStepIndex]));
  }

  // Creates the first step from user details
  getFirstStep(userDetails?: IUserDetails) {
    this.userDetails = userDetails;
    this.requestDetails = this.requestService.details;
    const stepOne = this.createStepOne(userDetails);
    this.steps = [stepOne, STEP_TWO, STEP_THREE, STEP_FOUR];

    return this.steps[0];
  }

  createStepOne(userDetails?: IUserDetails) {
    let stepOne = {} as IStep;

    // Without requestDetails, topic and option won't be available
    if (!this.requestDetails) {
      this.router.navigate([`/support`]);
    }

    stepOne = {
      ...STEP_ONE,
      data: {
        ...STEP_ONE.data,
        ...userDetails,
        topic: TOPICS[this.requestDetails && this.requestDetails.topic],
      },
      human: {
        ...STEP_ONE.human,
        ...userDetails,
        topic: TOPICS[this.requestDetails && this.requestDetails.topic]
      },
      analytics: {
        ...STEP_ONE.analytics,
        user_uuid: userDetails?.user_slug
      }
    };

    if (!this.errorCode) {
      this.analyticsService.trackEvent('Initialise', 'RequestFlow', 'start-flow', stepOne.analytics);
    } else {
      this.analyticsService.trackEvent('Initialise', 'RequestFlow', 'error-starting-flow', stepOne.analytics, { error: this.errorCode});
    }

    return stepOne;
  }

  updateCurrentStep(payload: IFlowPayload, infoBox?: IInfoBox) {
    let updatedStep = {} as any;
    const info = {
      heading: infoBox?.infoBoxHeading || '',
      content: infoBox?.infoBoxContent || '',
      type: infoBox?.infoBoxType,
      settings: {
        align: 'top'
      }
    };

    if (this.currentStepIndex === 3) {
      const sections = this.steps[this.currentStepIndex].sections;
      const customerCommentMarkup = `<p>${payload.customer_comment}</p>`;
      sections.forEach((section: any) => {
        if (section.uuid === 'message_section') {
          section.subtext = customerCommentMarkup.replace(/\s+/g, ' ').trim();
        }
      });
    }

    updatedStep = {
      ...this.steps[this.currentStepIndex],
      data: {
        ...this.steps[this.currentStepIndex].data,
        ...payload
      },
      human: {
        ...this.steps[this.currentStepIndex].human,
        ...payload
      },
      analytics: {
        ...this.steps[this.currentStepIndex].analytics,
      }
    };

    if (infoBox?.infoBoxContent || infoBox?.infoBoxHeading) {
      const infoSection = {
        uuid: `${this.steps[this.currentStepIndex].uuid}_info_section`,
        layout: '',
        settings: {},
        elements: [],
        info
      }
      updatedStep.sections.unshift(infoSection);
    }

    this.steps[this.currentStepIndex] = updatedStep;
  }

  private requestAssistedSupport(payload: IFlowPayload, resolve: (value: unknown) => void) {
    const requestPayload = {
      country: COMMON_CONSTANTS.appointmentsDefaultCountry,
      state: COMMON_CONSTANTS.appointmentsDefaultState,
      partner_slug: this.partnerBrandService.getAttribute('slug', 'partner'),
      first_name: payload.first_name,
      last_name: payload.last_name,
      email: payload.email,
      phone: payload.phone,
      date: payload.date,
      message: payload.customer_comment,
      // required attributes 'entity', 'entity_ref', 'topic', and 'option' is from this.requestDetails
      ...this.requestDetails
    } as ICreateAppointmentPayload;
    this.requestService.createRequest(requestPayload)
      .then((response) => {
        const payLoadForAnalytics = {
          country: COMMON_CONSTANTS.appointmentsDefaultCountry,
          state: COMMON_CONSTANTS.appointmentsDefaultState,
          partner_slug: this.partnerBrandService.getAttribute('slug', 'partner'),
          message: payload.customer_comment,
          ...this.requestDetails
        }
        if (response && response.error) {
          const { error } = response;
          let updatedStepOne =  {} as IStep;
          this.currentStepIndex = 0;
          this.errorCode = error.code ?? 'UNKNOWN';
          updatedStepOne = this.createStepOne(this.userDetails);
          this.steps[0] = updatedStepOne;
          const newPayload = Object.entries(payload).reduce<IFlowPayload>((acc, [key, value]) => {
            return key.includes('time') ? acc : { ...acc, [key]: value };
          }, {});
          this.updateCurrentStep(
            newPayload,
            { infoBoxContent: ERRORS[this.errorCode], infoBoxType: 'error' }
          );
          this.analyticsService.trackEvent('RequestAssistedSupport', 'RequestFlow', 'error', this.steps[this.currentStepIndex].analytics, { error: error.code } );
          resolve(this.steps[this.currentStepIndex]);
        } else {
          this.analyticsService.trackEvent(
            'RequestAssistedSupport',
            'RequestFlow',
            'request-assisted-support',
            this.steps[this.currentStepIndex].analytics,
            payLoadForAnalytics
          );
          this.currentStepIndex++;
          resolve(this.steps[this.currentStepIndex]);
        }
      }
    );
  }
}
